<template>
  <div>
    <!-- HERO 영역 -->
    <section class="hero">
      <div class="hero__overlay"></div>
      <header class="header">
        <AppLogo asLink to="/" size="md" :showText="true" />
      </header>
    </section>

    <!-- 메인 카드 -->
    <main class="main">
      <div class="container">
        <!-- Step 1: 입력 폼 -->
        <div v-if="step === 1" class="card trial-card">
          <div class="trial-inner">
            <div class="trial-head">
              <div class="trial-badge">무료체험 신청</div>
              <h1 class="trial-title">Free trial</h1>
            </div>

            <form class="trial-form" @submit.prevent="submitTrial">
              <UiInput
                placeholder="휴대폰 번호 ( - 제외)"
                type="tel"
                inputmode="tel"
                v-model="form.phone"
                autocomplete="tel"
              />

              <UiInput
                placeholder="체험 원하는 요일"
                v-model="form.preferredDay"
              />

              <UiInput placeholder="가능한 시간" v-model="form.preferredTime" />

              <UiButton
                type="submit"
                :disabled="!canSubmit || loading"
                :loading="loading"
              >
                신청하기
              </UiButton>
            </form>
          </div>
        </div>

        <!-- Step 2: 성공 화면 -->
        <div v-else class="card success-card">
          <div class="success-inner">
            <!-- 체크 아이콘 -->
            <div class="success-icon">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="50" fill="#2A4624" />
                <circle cx="50" cy="50" r="36" fill="#78FF47" />
                <path
                  d="M35 50 L45 60 L65 40"
                  stroke="#000000"
                  stroke-width="5"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <h2 class="success-title">신청이 완료되었습니다!</h2>
            <p class="success-message">
              담당자가 확인 후<br />
              <span class="phone-highlight">{{ formatPhone(form.phone) }}</span
              >(으)로<br />
              전화 안내해드리겠습니다.
            </p>

            <div class="success-actions">
              <UiButton @click="goToSignIn">확인</UiButton>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 푸터 -->
    <footer class="footer" style="margin-top: var(--space-64)">
      <div class="footer-bar">
        <span class="hint">Copyright ⓒ 2025 Cardio Factory</span>
      </div>
    </footer>
  </div>
</template>

<script>
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AppLogo from "@/components/AppLogo.vue";
import { api } from "@/lib/api";

export default {
  name: "FreeTrialView",
  components: { UiButton, UiInput, AppLogo },
  data() {
    return {
      step: 1, // 1 = 입력 폼, 2 = 성공 화면
      loading: false,
      form: {
        phone: "",
        preferredDay: "",
        preferredTime: "",
      },
    };
  },
  computed: {
    phoneValid() {
      const digits = (this.form.phone || "").replace(/\D/g, "");
      return digits.length >= 10 && digits.length <= 11;
    },
    canSubmit() {
      const { phone, preferredDay, preferredTime } = this.form;
      return phone && preferredDay && preferredTime && this.phoneValid;
    },
  },
  methods: {
    async submitTrial() {
      if (!this.canSubmit || this.loading) return;
      this.loading = true;

      try {
        const res = await api("/v1/free-trial", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone_number: this.form.phone.replace(/\D/g, ""),
            preferred_day: this.form.preferredDay,
            preferred_time: this.form.preferredTime,
          }),
        });

        if (!res.ok) throw new Error("무료체험 신청 실패");

        // 성공 화면으로 전환
        this.step = 2;
      } catch (e) {
        console.error(e);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
      } finally {
        this.loading = false;
      }
    },
    goToSignIn() {
      this.$router.push("/signin");
    },
    formatPhone(phone) {
      // "01012345678" -> "010-1234-5678"
      return phone.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    },
  },
};
</script>

<style scoped>
/* 입력 폼 카드 */
.trial-card {
  max-width: 758px;
  margin: 0 auto;
  padding: 48px 56px;
  background: #151718;
  border-radius: 30px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  position: relative;
  z-index: 10;
}

.trial-inner {
  max-width: 360px;
  margin: 0 auto;
}

/* 제목 */
.trial-head {
  margin-bottom: 24px;
  text-align: left;
}

.trial-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}

.trial-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

/* 폼 */
.trial-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

.trial-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important;
  background: #151718;
  color: #fff;
  padding: 0 14px;
}

.trial-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

.trial-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

.trial-form select.ui-input {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff;
  background: #151718;
  color: #fff;
  padding: 0 10px;
}

.trial-form .ui-btn {
  width: 100%;
}

/* 성공 화면 카드 */
.success-card {
  width: 480px;
  height: 597px;
  margin: 0 auto;
  background: #151718;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 10;
}

.success-inner {
  width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

/* 체크 아이콘 */
.success-icon {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

/* 제목 */
.success-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: -2px;
  color: #ffffff;
  text-align: center;
}

/* 메시지 */
.success-message {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 160%;
  text-align: center;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.8);
}

/* 버튼들 */
.success-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.btn-secondary {
  width: 100%;
  height: 54px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  border: 0;
  border-radius: 5px;
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: -2px;
  color: #000000;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-secondary:hover {
  opacity: 0.9;
}

.success-actions .ui-btn {
  width: 100%;
}

/* 푸터 */
.footer-bar {
  width: 100%;
  margin: 0;
  padding: 12px 16px;
  background: #151718;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
}
.hero {
  height: 220px; /* 기존 hero 높이가 크면 줄이기 */
  position: relative;
}

.hint {
  color: #8e8e8e;
  font-size: 14px;
}

.phone-highlight {
  color: #78ff47; /* 네온 그린 */
  font-weight: 600;
}

@media (max-width: 640px) {
  .trial-card,
  .success-card {
    padding: 28px 20px;
    border-radius: 20px;
    width: 100%;
    height: auto;
  }

  .trial-title,
  .success-title {
    font-size: 32px;
  }
}
</style>
